import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./Dashboard.css";

export default function ReportCard({ baseUrl, subjectForAssessment = [] }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [expandedSkills, setExpandedSkills] = useState({});
  const contentRef = useRef(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const downArrow = require("../icons/downarrow.png");

  const subjectHasData = (rows) => {
    if (!rows || rows.length === 0) return false;

    return rows.some((skill) => {
      const hasParentTally =
        skill.asmtPropTally && Object.keys(skill.asmtPropTally).length > 0;

      const hasExpectation = Object.values(skill.expectations || {}).some(
        (exp) =>
          exp.asmtPropTally && Object.keys(exp.asmtPropTally).length > 0
      );

      return hasParentTally || hasExpectation;
    });
  };

  //Down arrow
  useEffect(() => {
    const scrollContainer = document.querySelector(".main-content");
    if (!scrollContainer) return;

    const handleScroll = () => {
      const bottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 10;
      setShowScrollIndicator(!bottom);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(handleScroll);
    });

    resizeObserver.observe(scrollContainer);

    requestAnimationFrame(handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const studentkey = sessionStorage.getItem("studentkey");
        const districtID = sessionStorage.getItem("districtID");
        const token = sessionStorage.getItem("authToken");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const resp = await axios.get(
          `${baseUrl}Student/GetStudentReport?studentkey=${studentkey}&districtId=${districtID}`,
          { headers }
        );

        let d = resp.data;
        d = {
          subjectIDDisplay: d.subjectIDDisplay || d.SubjectIDDisplay || {},
          dataBySubjectId: d.dataBySubjectId || d.DataBySubjectId || {},
          teksDescription: d.teksDescription || d.TEKSDescription || {},
        };
        setData(d);
      } catch (err) {
        console.error("Error loading StudentReport:", err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [baseUrl]);

  // Initialize default subject selection once data & subject list load
  useEffect(() => {
    if (!data) return;

    const candidates = (subjectForAssessment || []).filter(
      (s) => s.subject && s.subject !== "All"
    );

    for (const s of candidates) {
      const key = Object.keys(data.subjectIDDisplay || {}).find(
        (k) =>
          data.subjectIDDisplay[k] === s.subject ||
          String(data.subjectIDDisplay[k]) === String(s.subject)
      );

      if (
        key &&
        subjectHasData(
          data.dataBySubjectId?.[key] ??
          data.dataBySubjectId?.[String(key)] ??
          []
        )
      ) {
        setSelectedSubjects([s.subject]);
        return;
      }
    }
  }, [data, subjectForAssessment]);

  const toggleSubject = (subject) => {
    setSelectedSubjects((prev) =>
      prev.includes(subject)
        ? prev.filter((s) => s !== subject)
        : [...prev, subject]
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span>Loading TEKS report...</span>
      </div>
    );
  }

  if (!data) {
    return <div className="no-data">No data available</div>;
  }

  const hexColorForPercentage = (pct) => {
    let cyan = 0;
    let magenta = 0;
    let yellow = 1;
    let black = 0;

    let delta = pct - 50;
    if (delta > 0) {
      cyan = delta / 50;
    } else {
      magenta = (-delta) / 50;
    }

    const red = Math.round(255 * (1 - cyan) * (1 - black));
    const green = Math.round(255 * (1 - magenta) * (1 - black));
    const blue = Math.round(255 * (1 - yellow) * (1 - black));

    return (
      "#" +
      red.toString(16).padStart(2, "0") +
      green.toString(16).padStart(2, "0") +
      blue.toString(16).padStart(2, "0")
    );
  };

  const getTextColor = (bgColor) => {
    if (!bgColor) return "#000";

    const c = bgColor.substring(1);
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
    return luminance > 130 ? "#000" : "#fff";
  };

  const buildColumnsForSubject = (subjectKey) => {
    const rows =
      data.dataBySubjectId?.[subjectKey] ??
      data.dataBySubjectId?.[String(subjectKey)];
    if (!rows || rows.length === 0) return [];

    const dict = {};

    const collectFromTally = (tallyObj) => {
      if (!tallyObj) return;
      for (const asmtId of Object.keys(tallyObj)) {
        const tally = tallyObj[asmtId];
        const ap = tally?.asmtProp;
        if (ap && (ap.ID || ap.id)) {
          dict[asmtId] = {
            asmtId,
            ID: ap.ID || ap.id,
            assessmentTypeDisplay: ap.assessmentTypeDisplay || "",
            assessmentCatgCode: ap.assessmentCatgCode || ap.ID || ap.id,
            documentTitle: ap.documentTitle || "",
            testDate: ap.testDate || null,
            sortOrder: ap.sortOrder || 0,
          };
        }
      }
    };

    rows.forEach((skill) => {
      collectFromTally(skill.asmtPropTally);
      for (const expKey of Object.keys(skill.expectations || {})) {
        collectFromTally(skill.expectations[expKey]?.asmtPropTally);
      }
    });

    const arr = Object.values(dict);
    arr.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    return arr;
  };

  const TEKSTable = ({ subjectKey }) => {
    const skills =
      data.dataBySubjectId?.[subjectKey] ??
      data.dataBySubjectId?.[String(subjectKey)] ??
      [];
    const columns = buildColumnsForSubject(subjectKey);

    const groups = [];
    if (columns.length) {
      let curr = columns[0].assessmentTypeDisplay || "";
      let count = 1;
      for (let i = 1; i < columns.length; i++) {
        if ((columns[i].assessmentTypeDisplay || "") !== curr) {
          groups.push({ label: curr, count });
          curr = columns[i].assessmentTypeDisplay || "";
          count = 1;
        } else {
          count++;
        }
      }
      groups.push({ label: curr, count });
    }

    return (
      <div
        className="panelitem ui-widget ui-corner-all"
        id={`TEKS${subjectKey}`}
      >
        <header className="ui-widget-header">
          {data.subjectIDDisplay[subjectKey] ??
            data.subjectIDDisplay[String(subjectKey)]}
        </header>
        <div className="panelcontent">
          <table className="TEKSPerformanceChart">
            <thead>
              <tr>
                <th colSpan={2}></th>
                {groups.map((g, idx) => (
                  <th key={idx} colSpan={g.count} className="district-header">
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr>
                <th colSpan={2}></th>
                {columns.map((col) => (
                  <th key={col.asmtId} className="bm-header">
                    {col.testDate ? (
                      <button type="button" title={col.documentTitle}>
                        {col.assessmentCatgCode}
                        <br />
                        <span className="date">
                          {new Date(col.testDate).toLocaleDateString()}
                        </span>
                      </button>
                    ) : (
                      col.assessmentCatgCode
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {skills.map((skill, sIdx) => {
                const skillKey = `${subjectKey}_${skill.skillNumber}`;
                const isExpanded = !!expandedSkills[skillKey];

                const parentTallyCells = columns.map((col) => {
                  const tally = skill.asmtPropTally?.[col.asmtId];
                  if (!tally)
                    return (
                      <td key={col.asmtId}>
                        <div className="colorblock empty" />
                      </td>
                    );
                  const color = hexColorForPercentage(tally.percentage);
                  return (
                    <td key={col.asmtId}>
                      <div
                        className="colorblock"
                        style={{
                          backgroundColor: color,
                          color: getTextColor(color),
                        }}
                      >
                        {tally.totalCorrect}/{tally.questionCount}
                      </div>
                    </td>
                  );
                });

                return (
                  <React.Fragment key={sIdx}>
                    <tr className="Chartparent">
                      <td>
                        <button
                          type="button"
                          className="skillBtn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSkills((prev) => ({
                              ...prev,
                              [skillKey]: !prev[skillKey],
                            }));
                          }}
                          aria-expanded={isExpanded}
                        >
                          {skill.skillNumber}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="descBtn"
                          title={
                            data.teksDescription?.[`${skill.skillNumber}>`] ??
                            ""
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          ?
                        </button>
                      </td>
                      {parentTallyCells}
                    </tr>

                    {Object.values(skill.expectations || {}).map(
                      (exp, eIdx) => (
                        <tr
                          key={`exp-${sIdx}-${eIdx}`}
                          className="child"
                          style={{
                            display: isExpanded ? "table-row" : "none",
                          }}
                        >
                          <td>{exp.expectationLetter}</td>
                          <td>
                            <button
                              type="button"
                              className="descBtn"
                              title={
                                data.teksDescription?.[
                                `${skill.skillNumber}>${exp.expectationLetter}`
                                ] ?? ""
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              ?
                            </button>
                          </td>
                          {columns.map((col) => {
                            const tally = exp.asmtPropTally?.[col.asmtId];
                            if (!tally)
                              return (
                                <td key={col.asmtId}>
                                  <div className="colorblock empty" />
                                </td>
                              );
                            const color =
                              hexColorForPercentage(tally.percentage);
                            return (
                              <td key={col.asmtId}>
                                <div
                                  className="colorblock"
                                  style={{
                                    backgroundColor: color,
                                    color: getTextColor(color),
                                  }}
                                >
                                  {tally.totalCorrect}/{tally.questionCount}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      )
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div>
        {/* Subject Buttons Row with Icons — Filtered to only subjects with TEKS data */}
        <div className="subject-row">
          {(subjectForAssessment || [])
            .filter(({ subject }) => {
              if (!subject || subject === "All") return false;

              const subjectKey = Object.keys(data.subjectIDDisplay || {}).find(
                (k) =>
                  data.subjectIDDisplay[k] === subject ||
                  String(data.subjectIDDisplay[k]) === String(subject)
              );

              return subjectKey
                ? subjectHasData(
                  data.dataBySubjectId?.[subjectKey] ??
                  data.dataBySubjectId?.[String(subjectKey)] ??
                  []
                )
                : false;
            })
            .map(({ subject, icon }) => (
              <button
                key={subject}
                className={`subject-button ${selectedSubjects.includes(subject) ? "active" : ""
                  }`}
                onClick={() => toggleSubject(subject)}
              >
                {subject && icon ? (
                  <img
                    src={icon}
                    alt={`${subject} icon`}
                    className="subject-icon"
                  />
                ) : (
                  <img
                    src={require("../icons/ALL.png")}
                    alt="icon"
                    className="subject-icon"
                  />
                )}
                {subject}
              </button>
            ))}
        </div>

        {/* TEKS Tables */}
        <div style={{ overflow: "auto", WebkitOverflowScrolling: "touch" }}>
          <div
            id="studentReportCard"
            className="grid-container"
            ref={contentRef}
          >
            {Object.keys(data.subjectIDDisplay || {})
              .filter((key) =>
                selectedSubjects.includes(
                  data.subjectIDDisplay[key] ??
                  data.subjectIDDisplay[String(key)]
                )
              )
              .map((subjectKey) => (
                <TEKSTable key={subjectKey} subjectKey={subjectKey} />
              ))}
          </div>
        </div>
      </div>

      {showScrollIndicator && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#d0cfcf99",
            color: "white",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            zIndex: 9999,
            cursor: "pointer",
            animation: "bounce 1.5s infinite",
          }}
          onClick={() =>
            window.scrollBy({ top: 200, behavior: "smooth" })
          }
        >
          <img
            src={downArrow}
            alt="Scroll down"
            style={{ width: "22px", height: "22px" }}
          />
        </div>
      )}
    </>
  );
}